import { useMemo, useState } from "react";

import type { MemberCustomerVo } from "@zhihaoscm/entity";
import { dashboardService } from "@/api/services/dashboardService";
import { formatThousandSeparator } from "@/utils/money";
import DashboardChart from "../chart";
import { getAnnularOption } from "../chartDefaultOption";
import {
    DashboardDateSelect,
    type DashboardDateValue,
    DataWrap,
    toDashboardDateParams,
    useDashboardQuery,
    WidgetBody
} from "../dashboardWidgets";

const MEMBER_COLORS = ["#1777ff", "#ffae4c", "#6fd195"];

/** 会员用户统计，严格沿用 Vue 的总量字号、环形图和三类会员图例。 */
export default function MemberCustomerStatistic() {
    const [time, setTime] = useState<DashboardDateValue>(1);
    const { data, loading, error } = useDashboardQuery<Partial<MemberCustomerVo>>(
        () => dashboardService.getMemberCustomerStatistic(toDashboardDateParams(time)),
        [time]
    );
    const seriesData = useMemo(
        () => [
            { value: data?.totalBaseMemberNum || 0, name: "基础会员" },
            { value: data?.totalSeniorMemberNum || 0, name: "高级会员" },
            { value: data?.totalEnterpriseGradeMemberNum || 0, name: "企业会员" }
        ],
        [data]
    );
    const total = seriesData.reduce((sum, item) => sum + item.value, 0);
    const option = useMemo(
        () =>
            getAnnularOption({
                legend: {
                    itemGap: 24,
                    left: "60%",
                    top: "middle",
                    textStyle: {
                        rich: {
                            a: { width: 80, color: "#333333", fontSize: 14 },
                            b: { color: "#333333", fontSize: 14 }
                        }
                    },
                    formatter(name: string) {
                        const item = seriesData.find(seriesItem => seriesItem.name === name);
                        return item ? `{a|${name}}{b| ${formatThousandSeparator(item.value, 0)}人}` : name;
                    }
                },
                series: [
                    {
                        radius: ["50%", "65%"],
                        center: ["30%", "50%"],
                        data: seriesData,
                        itemStyle: {
                            color(params: { dataIndex: number }) {
                                return MEMBER_COLORS[params.dataIndex % MEMBER_COLORS.length];
                            }
                        },
                        emphasis: {
                            label: {
                                show: true,
                                formatter(item: { name: string; value: number }) {
                                    const rate = total ? (item.value / total) * 100 : 0;
                                    return [`{a|${item.name}}`, `{b|${formatThousandSeparator(rate)}%}`].join("\n");
                                },
                                rich: {
                                    a: { color: "#999999e6", fontSize: 14, lineHeight: 25 },
                                    b: { color: "#333333e6", fontSize: 20, verticalAlign: "bottom" }
                                }
                            }
                        }
                    }
                ]
            }),
        [seriesData, total]
    );

    return (
        <DataWrap
            title="会员用户统计"
            className="member-customer-statistic"
            option={
                <DashboardDateSelect
                    value={time}
                    onChange={setTime}
                    options={["all", "month", "quarter", "year"]}
                    defaultActive="all"
                />
            }
        >
            <WidgetBody loading={loading} error={error}>
                <div className="member-statistic-main">
                    <div className="dashboard-statistic-text-box">
                        <span className="dashboard-statistic-label">累计会员用户</span>
                        <span className="dashboard-statistic-value">
                            {formatThousandSeparator(data?.totalMemberNum || 0, 0)}
                            <i>人</i>
                        </span>
                    </div>
                    <div className="member-customer-chart">
                        <DashboardChart option={option} ariaLabel="会员用户构成环形图" />
                    </div>
                </div>
            </WidgetBody>
        </DataWrap>
    );
}
